import {
    ChangeDetectorRef,
    Component,
    inject,
    OnInit,
    AfterContentChecked,
    Signal,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { FileSizePipe } from 'ngx-filesize';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, Observable } from 'rxjs';
import { AlertService, ButtonColors } from '@ed---interne/ng-uui-components';

import {
    OrganizationState,
    UpdateConcessionControl,
    UpdateCracMap,
    SynchronizeConcessionControl,
    SynchronizeCracMap,
} from '../organization.state';
import { ConcessionControl } from 'src/app/core/models/concession-control.model';
import { FilesizeOptions } from 'src/app/shared/all.constants';
import {
    ConcessionControlStatus,
    CC_STATUS_CONSTANTS,
} from 'src/app/features/main/concessioncontrol/assets/constants';
import { Organization } from 'src/app/core/models/organization.model';
import { CracMap } from 'src/app/core/models/crac-map.model';
import { CRACMAP_STATUS_CONSTANTS } from 'src/app/features/main/cracmap/assets/constants';

@UntilDestroy()
@Component({
    selector: 'organization-manage-studies',
    templateUrl: './organization-manage-studies.component.html',
    styleUrls: ['./organization-manage-studies.component.scss'],
    providers: [FileSizePipe],
})
export class OrganizationManageStudiesComponent implements OnInit, AfterContentChecked {
    public organizationSignal: Signal<Organization> = this.store.selectSignal(
        OrganizationState.currentOrganization,
    ) as Signal<Organization>;
    public editingStudies = new Set<string>();
    public studiesFormGroups: { [key: string]: FormGroup } = {};
    public originalStudiesFormGroup: { [key: string]: any } = {};

    public buttonColors = ButtonColors;
    public ccStatusOptions = CC_STATUS_CONSTANTS;

    public ccStudies: ConcessionControl[] = [];
    public cracMaps: CracMap[] = [];
    public editingCracMaps = new Set<string>();
    public cracMapsFormGroups: { [key: string]: FormGroup } = {};
    public originalCracMapsFormGroup: { [key: string]: any } = {};

    private currentOrganization$: Observable<Organization | null> = inject(Store).select(
        OrganizationState.currentOrganization,
    );
    public cracMapStatusOptions = CRACMAP_STATUS_CONSTANTS;

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly store: Store,
        private readonly cdr: ChangeDetectorRef,
        private readonly router: Router,
        private readonly alertService: AlertService,
        private readonly fileSizePipe: FileSizePipe,
    ) {}

    ngOnInit() {
        this.currentOrganization$.pipe(untilDestroyed(this)).subscribe((currentOrganization) => {
            this.cracMaps = currentOrganization?.cracMaps?.sort((a, b) => b.year - a.year) || [];
            this.cracMaps.forEach((cracMap) => {
                if (!this.cracMapsFormGroups[cracMap.id]) {
                    this.cracMapsFormGroups[cracMap.id] = this.formBuilder.group({
                        id: [cracMap.id, Validators.required],
                        status: [cracMap.status, Validators.required],
                        year: [cracMap.year, Validators.required],
                    });
                }

                this.originalCracMapsFormGroup[cracMap.id] = {
                    ...this.cracMapsFormGroups[cracMap.id].value,
                };
            });

            this.ccStudies =
                currentOrganization?.concessionControls?.sort((a, b) => b.year - a.year) || [];
            this.ccStudies.forEach((ccStudy) => {
                if (!this.studiesFormGroups[ccStudy.id]) {
                    this.studiesFormGroups[ccStudy.id] = this.formBuilder.group({
                        id: [ccStudy.id, Validators.required],
                        status: [ccStudy.status, Validators.required],
                        description: [ccStudy.description, Validators.required],
                        document: [ccStudy.document, Validators.required],
                        year: [ccStudy.year, Validators.required],
                        isFileDeleted: [false],
                        isNewFile: [false],
                        sizeFile: [ccStudy.sizeFile || 0],
                    });
                }

                this.originalStudiesFormGroup[ccStudy.id] = {
                    ...this.studiesFormGroups[ccStudy.id].value,
                    isFileDeleted: false,
                    isNewFile: false,
                };
            });
        });
    }

    ngAfterContentChecked(): void {
        this.cdr.detectChanges();
    }
    getBadgeLabel(statusId: number): string {
        const option = this.ccStatusOptions.find((option) => option.value === statusId);
        return option ? option.label : '';
    }

    getBadgeClass(status: number): string {
        switch (status) {
            case ConcessionControlStatus.AWAITING_THE_ANNUAL_ORDER:
                return 'pending';
            case ConcessionControlStatus.WAITING:
                return 'inProgress';
            case ConcessionControlStatus.PROCESSING:
                return 'inProgress';
            default:
                return 'available';
        }
    }

    ///////////// CONCESSION CONTROL /////////////////

    openConcessionControl(year: number) {
        const organization = this.organizationSignal();
        this.router.navigate([`main/${organization?.id}/cc/${year}/`]);
    }

    editStudy(id: string): void {
        this.editingStudies.add(id);
    }

    synchronizeCC(id: string): void {
        const ccStudy = this.ccStudies.find((ccStudy: ConcessionControl) => ccStudy.id === id);
        ccStudy!.isSynchronizing = true;
        this.store
            .dispatch(
                new SynchronizeConcessionControl({
                    year: ccStudy!.year,
                }),
            )
            .subscribe({
                next: () => {
                    this.alertService.valid(
                        `Contrôle de concession ${ccStudy!.year}`,
                        "L'exercice a bien été synchronisé",
                    );
                    ccStudy!.isSynchronizing = false;
                },
                error: (err) => {
                    this.alertService.error(
                        `Contrôle de concession ${ccStudy!.year}`,
                        `Une erreur est survenue : ${err}`,
                        10000,
                    );
                    ccStudy!.isSynchronizing = false;
                },
            });
    }

    saveChangesStudy(id: string): void {
        const ccStudy = this.ccStudies.find((ccStudy: ConcessionControl) => ccStudy.id === id);
        ccStudy!.isLoading = true;

        const updatedData = this.studiesFormGroups[id].value;

        const organization = this.organizationSignal();
        const updatedDataToUpdate: Partial<ConcessionControl> = {
            id: updatedData.id,
            status: updatedData.status,
            description: updatedData.description,
            year: updatedData.year,
            organization: {
                id: organization?.id ?? '',
                name: '',
                clientCode: '',
                departmentCode: '',
                userCount: 0,
                concessionControls: [],
                cracs: [],
                cracMaps: [],
            },
        };

        const formGroup = this.studiesFormGroups[id];
        let file = formGroup.get('document')?.value;

        const isFileDeleted = formGroup.value.isFileDeleted;
        const isNewFile = formGroup.value.isNewFile;

        if (isFileDeleted && !isNewFile) {
            file = null;
        }

        this.store
            .dispatch(
                new UpdateConcessionControl({
                    concessionControlPart: updatedDataToUpdate,
                    file,
                    isFileDeleted,
                    isNewFile,
                }),
            )
            .subscribe({
                next: () => {
                    this.alertService.valid(
                        'Contrôle de concession',
                        "L'exercice a bien été modifié",
                    );
                    file = null;
                    formGroup.patchValue({
                        isNewFile: false,
                        isFileDeleted: false,
                    });

                    ccStudy!.isLoading = false;
                    this.editingStudies.delete(id);
                },
                error: (err) => {
                    this.alertService.error(
                        'Contrôle de concession',
                        "Une erreur est survenue : Impossible de mettre à jour l'exercice",
                    );
                },
            });
    }

    cancelEditStudy(id: string): void {
        this.editingStudies.delete(id);
        this.studiesFormGroups[id].reset(this.originalStudiesFormGroup[id]);
    }

    downloadFile() {}

    deleteFile(id: string): void {
        const formGroup = this.studiesFormGroups[id];
        const currentFile = formGroup.get('document')?.value;
        if (currentFile) {
            formGroup.patchValue({ document: null, isFileDeleted: true });
            formGroup.get('document')?.markAsDirty();
            formGroup.get('document')?.updateValueAndValidity();
        }
    }

    public getFileSize(fileSize: number): string {
        return String(this.fileSizePipe.transform(fileSize, FilesizeOptions));
    }

    public onFileDropped(event: any, id: string): void {
        this.onFileChange(event, id);
    }

    public onFileSelect(event: any, id: string): void {
        this.onFileChange(event.target.files, id);
    }

    public onFileChange(event: any, id: string): void {
        if (!event.length) {
            return;
        }
        const document = event[0];

        if (document) {
            this.studiesFormGroups[id].patchValue({ isNewFile: true });
            this.studiesFormGroups[id].get('document')?.markAsDirty();
            this.studiesFormGroups[id].get('document')?.updateValueAndValidity();
            this.studiesFormGroups[id].patchValue({
                document: document,
                sizeFile: document.size,
            });
        }
    }

    ///////////// CRAC MAPS /////////////////

    openCracMap(year: number) {
        const organization = this.organizationSignal();
        this.router.navigate([`main/${organization?.id}/cracmap/${year}/`]);
    }

    editCracMap(id: string): void {
        this.editingCracMaps.add(id);
    }

    synchronizeCracMap(id: string): void {
        const cracMap = this.cracMaps.find((cm: CracMap) => cm.id === id);
        cracMap!.isSynchronizing = true;
        this.store
            .dispatch(
                new SynchronizeCracMap({
                    year: cracMap!.year,
                }),
            )
            .subscribe({
                next: () => {
                    this.alertService.valid(
                        `Cartes nationales CRAC ${cracMap!.year}`,
                        "L'année a bien été synchronisé",
                    );
                    cracMap!.isSynchronizing = false;
                },
                error: (err) => {
                    this.alertService.error(
                        `Cartes nationales CRAC ${cracMap!.year}`,
                        `Une erreur est survenue : ${err}`,
                        10000,
                    );
                    cracMap!.isSynchronizing = false;
                },
            });
    }

    saveChangesCracMap(id: string): void {
        const cracMap = this.cracMaps.find((cm: CracMap) => cm.id === id);
        if (!cracMap) return;
        cracMap!.isLoading = true;

        const updatedData = this.cracMapsFormGroups[id].value;

        const organization = this.organizationSignal();
        const updatedDataToUpdate: Partial<ConcessionControl> = {
            id: updatedData.id,
            status: updatedData.status,
            year: updatedData.year,
            organization: {
                id: organization?.id ?? '',
                name: '',
                clientCode: '',
                departmentCode: '',
                userCount: 0,
                concessionControls: [],
                cracs: [],
                cracMaps: [],
            },
        };

        this.store
            .dispatch(
                new UpdateCracMap({
                    cracMap: updatedDataToUpdate,
                }),
            )
            .subscribe({
                next: () => {
                    this.alertService.valid(
                        `Carte CRAC ${updatedData.year}`,
                        "L'exercice a bien été modifié",
                    );
                    cracMap!.isLoading = false;
                    this.editingCracMaps.delete(id);
                },
                error: (err) => {
                    this.alertService.error(
                        `Carte CRAC ${updatedData.year}`,
                        "Une erreur est survenue : Impossible de mettre à jour l'exercice",
                    );
                },
            });
    }
    cancelEditCracMap(id: string): void {
        this.editingCracMaps.delete(id);
        this.cracMapsFormGroups[id].reset(this.originalCracMapsFormGroup[id]);
    }
}
